import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, HeartHandshake } from "lucide-react";
import { HERO } from "@/lib/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1555819206-7b30da4f1506?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGh1Z2dpbmclMjBob3BlZnVsfGVufDB8fHx8MTc4Njc2NjkzMnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const lineVariants = {
    hidden: { y: "110%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-bone pt-28 pb-20 sm:pt-36 lg:pb-28"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        {/* Left: kinetic text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-line bg-sand px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-sage"
          >
            <HeartHandshake className="h-4 w-4 text-terracotta" strokeWidth={2.2} />
            {HERO.eyebrow}
          </motion.div>

          <h1 className="font-serif text-[clamp(3rem,9vw,7rem)] font-medium leading-[0.95] tracking-tight text-ink">
            {HERO.lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={reduce ? undefined : lineVariants}
                  initial={reduce ? { opacity: 0 } : "hidden"}
                  animate={reduce ? { opacity: 1 } : "show"}
                  className="block"
                >
                  {i === HERO.lines.length - 1 ? (
                    <span className="italic text-terracotta">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone sm:text-lg"
          >
            {HERO.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#hotlines"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(193,119,103,0.35)] transition-transform duration-300 hover:scale-105 hover:bg-[#a9614f]"
            >
              {HERO.primaryCta}
            </a>
            <a
              href="#promise"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-line bg-transparent px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-sand"
            >
              {HERO.secondaryCta}
            </a>
          </motion.div>
        </div>

        {/* Right: parallax clipped image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-[999px_999px_28px_28px] border border-line shadow-[0_30px_80px_rgba(45,48,42,0.14)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Women holding hands in support of one another"
              style={reduce ? undefined : { y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#promise"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="mx-auto mt-14 flex w-fit flex-col items-center gap-2 text-xs uppercase tracking-[0.2em] text-sage"
        data-testid="hero-scroll-hint"
      >
        Scroll
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.a>
    </section>
  );
}
